import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Container } from '@mui/material';
import toast from 'react-hot-toast';
import { TransactionHeader, LineItemsTable } from './';
import { ConfirmDialog } from '../common';
import { getTransactionAPI, itemsAPI, quotesAPI } from '../../api';
import { downloadTransactionExcel } from '../../utils/fileDownload';
import { MAX_LINE_ITEMS, TRANSACTION_TYPES, createEmptyLineItem } from '../../constants';
import { fetchActiveAccounts, selectActiveAccounts } from '../../store/accountsSlice';

// FK field each transaction type's line items are attached through - mirrors
// the shape POST /items already expects for cash_sales_id/invoices_id/etc,
// with quotations_id added for the new Quote type.
const TRANSACTION_FK_FIELDS = {
  [TRANSACTION_TYPES.CASH_SALE]: 'cash_sales_id',
  [TRANSACTION_TYPES.INVOICE]: 'invoices_id',
  [TRANSACTION_TYPES.CREDIT_NOTE]: 'credit_notes_id',
  [TRANSACTION_TYPES.REFUND]: 'refunds_id',
  [TRANSACTION_TYPES.QUOTE]: 'quotations_id',
};

const TransactionForm = ({ type }) => {
  const dispatch = useDispatch();

  // "Continue" support: a Quote's list page can navigate here with prefill
  // data in router state (customer/account, order number, items, and the
  // source quote's id) so an existing quote can be turned into a real
  // Cash Sale/Account Invoice without retyping everything.
  const location = useLocation();
  const prefill = location.state || {};

  // Form state
  const [customerName, setCustomerName] = useState(prefill.customerName || '');
  const [account, setAccount] = useState(prefill.account || null);
  const accounts = useSelector(selectActiveAccounts);
  const [orderNumber, setOrderNumber] = useState(prefill.orderNumber || '');
  const [vatExempt, setVatExempt] = useState(prefill.vatExempt || false);
  const [items, setItems] = useState(
    prefill.items && prefill.items.length > 0 ? prefill.items : [createEmptyLineItem()]
  );
  // Only relevant when type === TRANSACTION_TYPES.QUOTE - toggles whether the
  // quote is being made out to a cash customer or an account, since a Quote
  // isn't inherently one or the other the way Cash Sale/Invoice are.
  const [quoteMode, setQuoteMode] = useState('cash');
  const [sourceQuoteId, setSourceQuoteId] = useState(prefill.sourceQuoteId || null);

  // Validation and totals state
  const [totals, setTotals] = useState({ subtotal: 0, vat: 0, total: 0 });
  const [validation, setValidation] = useState({ ready: false, vat_ready: false });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Confirmation dialog state
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const isInvoiceOrCredit = type === TRANSACTION_TYPES.INVOICE || type === TRANSACTION_TYPES.CREDIT_NOTE;
  const isQuote = type === TRANSACTION_TYPES.QUOTE;
  // "Save as Quote" only makes sense from the two forms that create a real
  // sale going forward (Cash Sale, Account Invoice) - not Refund/Credit Note
  // (crediting money back isn't something you'd quote), and not from the
  // Quote form itself.
  const canSaveAsQuote = type === TRANSACTION_TYPES.CASH_SALE || type === TRANSACTION_TYPES.INVOICE;

  // Load active accounts if needed (for invoices/credits, and for a Quote
  // that may be switched into "Account" mode) - shared Redux cache, so
  // switching between forms doesn't refetch every time.
  const needsAccounts = isInvoiceOrCredit || isQuote;
  useEffect(() => {
    if (needsAccounts && accounts.length === 0) {
      dispatch(fetchActiveAccounts()).catch(() => {
        toast.error('Failed to load accounts');
      });
    }
  }, [needsAccounts, accounts.length, dispatch]);

  // Handle validation and totals update from LineItemsTable
  const handleValidationChange = useCallback(({ totals: newTotals, validation: newValidation }) => {
    setTotals(newTotals);
    setValidation(newValidation);
  }, []);

  // Add new row
  const handleAddRow = () => {
    if (items.length >= MAX_LINE_ITEMS) {
      toast.error(`Maximum ${MAX_LINE_ITEMS} rows allowed`);
      return;
    }

    const previousItem = items[items.length - 1];
    const newItem = {
      ...createEmptyLineItem(),
      registration: previousItem.registration || '',
      discount: previousItem.discount !== undefined ? previousItem.discount : 0,
    };

    setItems([...items, newItem]);
    toast.success('Row added');
  };

  // Clear form with confirmation
  const handleClearClick = () => {
    setShowClearConfirm(true);
  };

  const handleClearConfirm = () => {
    setCustomerName('');
    setAccount(null);
    setOrderNumber('');
    setVatExempt(false);
    setItems([createEmptyLineItem()]);
    setQuoteMode('cash');
    setSourceQuoteId(null);
    setShowClearConfirm(false);
    toast.success('Form cleared');
  };

  // Shared submit logic for both a normal submit (targetType === this form's
  // own `type`) and "Save as Quote" (targetType === TRANSACTION_TYPES.QUOTE
  // from a Cash Sale/Invoice form) - one implementation instead of two
  // parallel copies of the same create-transaction/create-items/download flow.
  const submitAs = async (targetType, useAccount) => {
    if (!validation.ready) {
      toast.error('Please complete all fields');
      return;
    }

    if (!validation.vat_ready) {
      toast.error('All items must have the same VAT rate');
      return;
    }

    if (useAccount && !account) {
      toast.error('Please select an account');
      return;
    }

    // Unlike a real Cash Sale (which can stay anonymous), a cash-style Quote
    // needs a customer name - otherwise there's nothing to find it by later
    // when someone comes back to continue or reference it.
    if (targetType === TRANSACTION_TYPES.QUOTE && !useAccount && !customerName.trim()) {
      toast.error('Customer name is required for a quote');
      return;
    }

    setIsSubmitting(true);

    try {
      // Step 1: Prepare transaction data
      const transactionData = {
        subtotal: totals.subtotal.toFixed(2),
        vat: totals.vat.toFixed(2),
        total: totals.total.toFixed(2),
        order_number: orderNumber || null,
      };

      if (useAccount) {
        transactionData.accounts_id = account.id;
      } else {
        transactionData.customer = customerName || null;
      }

      // Step 2: Create transaction
      const targetAPI = getTransactionAPI(targetType);
      const transactionResponse = await targetAPI.create(transactionData);
      const transactionId = transactionResponse.data.id;

      // Step 3: Create line items
      const fkField = TRANSACTION_FK_FIELDS[targetType];
      const itemsData = items.map(item => ({
        [fkField]: transactionId,
        part_number: item.part_number || null,
        description: item.description || null,
        registration: item.registration || null,
        qty: item.qty,
        price: item.price,
        discount: item.discount,
        subtotal: item.subtotal.toFixed(2),
        vat: item.vat.toFixed(2),
        vat_perc: item.vat_perc,
        total: item.total.toFixed(2),
      }));

      await itemsAPI.createBulk(itemsData);

      // Step 4: Download Excel file
      const pdfResponse = await targetAPI.downloadPDF(transactionId);
      downloadTransactionExcel(pdfResponse, targetType, transactionId);

      // Step 5: If this was a real sale (not a quote save) that originated
      // from a quote, mark that source quote converted+deleted. The actual
      // transaction has already succeeded at this point, so a failure here
      // shouldn't surface as an error to the user - just log it.
      if (targetType !== TRANSACTION_TYPES.QUOTE && sourceQuoteId) {
        try {
          await quotesAPI.update(sourceQuoteId, {
            converted_transaction_type: targetType,
            converted_transaction_id: transactionId,
          });
        } catch (conversionError) {
          console.warn('Failed to mark source quote as converted:', conversionError);
        }
      }

      // Step 6: Success - clear form
      const successMessage = targetType === TRANSACTION_TYPES.QUOTE
        ? `Quote ${transactionId} saved successfully!`
        : `Transaction ${transactionId} created successfully!`;
      toast.success(successMessage);
      handleClearConfirm(); // Clear without confirmation

    } catch (error) {
      console.error('Submit error:', error);
      const errorMessage = error.response?.data?.message ||
        (targetType === TRANSACTION_TYPES.QUOTE ? 'Failed to save quote' : 'Failed to create transaction');
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Whether this form's own submit should send accounts_id (true) or
  // customer (false). For the Quote form itself this depends on the
  // Cash Customer/Account toggle; for every other form it's fixed by type.
  const submitUsesAccount = isQuote ? quoteMode === 'account' : isInvoiceOrCredit;

  const handleSubmit = () => submitAs(type, submitUsesAccount);

  // "Save as Quote" mirrors whatever field the origin form (Cash Sale or
  // Account Invoice) already uses - not the Quote form's own toggle, which
  // is irrelevant here since we're not on the Quote form.
  const handleSaveAsQuote = () => submitAs(TRANSACTION_TYPES.QUOTE, type === TRANSACTION_TYPES.INVOICE);

  // Determine if can submit. A cash-style Quote (this form, in cash mode)
  // additionally needs a customer name - see the matching check in submitAs.
  const canSubmit = validation.ready && validation.vat_ready
    && (!submitUsesAccount || account)
    && (!(isQuote && quoteMode === 'cash') || customerName.trim());

  // Save as Quote's own enablement (Cash Sale/Invoice only - see
  // canSaveAsQuote above): mirrors whichever field handleSaveAsQuote will
  // actually send, which is NOT the same thing canSubmit checks for this
  // form's own type - Cash Sale's normal submit is fine with no customer
  // name, but saving it as a quote is not.
  const saveAsQuoteEnabled = validation.ready && validation.vat_ready
    && (type === TRANSACTION_TYPES.INVOICE ? Boolean(account) : Boolean(customerName.trim()));

  const canAddRow = items.length < MAX_LINE_ITEMS;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <TransactionHeader
        type={type}
        customerName={customerName}
        onCustomerNameChange={setCustomerName}
        account={account}
        accounts={accounts}
        onAccountChange={setAccount}
        orderNumber={orderNumber}
        onOrderNumberChange={setOrderNumber}
        vatExempt={vatExempt}
        onVatExemptChange={setVatExempt}
        quoteMode={quoteMode}
        onQuoteModeChange={setQuoteMode}
        totals={totals}
        onAddRow={handleAddRow}
        onClear={handleClearClick}
        onSubmit={handleSubmit}
        showSaveAsQuote={canSaveAsQuote}
        onSaveAsQuote={handleSaveAsQuote}
        saveAsQuoteEnabled={saveAsQuoteEnabled}
        canAddRow={canAddRow}
        canSubmit={canSubmit}
        isSubmitting={isSubmitting}
      />

      <LineItemsTable
        items={items}
        onChange={setItems}
        onValidationChange={handleValidationChange}
        vatExempt={vatExempt}
      />

      <ConfirmDialog
        open={showClearConfirm}
        title="Clear Form"
        message="Are you sure you want to clear all data?"
        onConfirm={handleClearConfirm}
        onCancel={() => setShowClearConfirm(false)}
        confirmText="Clear"
        confirmColor="error"
      />
    </Container>
  );
};

export default TransactionForm;
